import { useCallback, useEffect, useRef, useState } from 'react';
import type { OperationalPicture, OperationalZone } from '@ddp/shared';

import { apiFetch } from '../../api/client.js';
import { readPictureStream } from './operations-stream.js';

/** Liste des zones défendues de l'organisation. `reload` la rafraîchit après une création/mise à jour. */
export function useOperationalZones() {
  const [zones, setZones] = useState<OperationalZone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Jeton de rechargement : l'incrémenter relance l'effet de chargement.
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    apiFetch<OperationalZone[]>('/ops/zones')
      .then((res) => {
        if (active) setZones(res);
      })
      .catch((err: unknown) => {
        if (active)
          setError(err instanceof Error ? err.message : 'Chargement des zones impossible.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [tick]);

  return { zones, loading, error, reload };
}

/** Cadence du polling de repli — alignée sur le flux SSE et le tick du simulateur (ADR-0040). */
const POLL_INTERVAL_MS = 5000;
/** Délai de reconnexion du flux SSE après une chute (le polling comble l'intervalle). */
const SSE_RETRY_MS = 5000;
/** Grâce laissée au SSE pour livrer sa première trame avant de lancer le polling de repli. */
const SSE_GRACE_MS = 1500;

/**
 * Situation consolidée d'une zone (ADR-0040 §6, C4). Le **transport est SSE** (push continu) derrière
 * la même interface qu'auparavant — l'IHM n'a pas changé. Le **polling reste un repli** : si le flux
 * tarde à livrer, chute ou n'est pas supporté, on bascule en polling et on retente le SSE en tâche de
 * fond ; dès qu'il relivre, le polling s'arrête. Tout est suspendu quand l'onglet est caché et reprend
 * au retour. `null` tant qu'aucune zone n'est sélectionnée.
 */
export function useOperationalPicture(zoneId: string | null) {
  const [picture, setPicture] = useState<OperationalPicture | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // `refresh` est recâblé par l'effet sur la zone courante : on le tient dans une ref pour garder une
  // identité stable côté appelant (les actions de gestion C3 l'appellent après chaque mutation).
  const refreshRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    setError(null);
    setPicture(null);
    if (!zoneId) {
      refreshRef.current = () => undefined;
      return;
    }
    const id = zoneId;
    setLoading(true);

    let aborted = false;
    let gotFirst = false;
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let graceTimer: ReturnType<typeof setTimeout> | null = null;
    let sse: AbortController | null = null;

    const apply = (p: OperationalPicture) => {
      gotFirst = true;
      if (!aborted) {
        setPicture(p);
        setError(null);
        setLoading(false);
      }
    };

    const pollOnce = async () => {
      try {
        apply(await apiFetch<OperationalPicture>(`/ops/zones/${id}/picture`));
      } catch (err) {
        if (!aborted) {
          setError(err instanceof Error ? err.message : 'Situation indisponible.');
          setLoading(false);
        }
      }
    };
    const startPolling = () => {
      if (pollTimer === null) {
        void pollOnce();
        pollTimer = setInterval(() => void pollOnce(), POLL_INTERVAL_MS);
      }
    };
    const stopPolling = () => {
      if (pollTimer !== null) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    };

    const connectSse = async () => {
      sse = new AbortController();
      try {
        // Chaque trame arrête le polling de repli : le SSE redevient la source.
        await readPictureStream(
          id,
          (p) => {
            stopPolling();
            apply(p);
          },
          sse.signal,
        );
      } catch {
        // Erreur de flux : on retombe sur le polling ci-dessous.
      }
      if (aborted || sse.signal.aborted) return;
      // Flux tombé (fin propre ou erreur) : garantir la donnée par polling, puis retenter le SSE.
      startPolling();
      reconnectTimer = setTimeout(() => {
        if (!aborted) void connectSse();
      }, SSE_RETRY_MS);
    };

    const start = () => {
      gotFirst = false;
      void connectSse();
      // Si le SSE n'a rien livré rapidement (indispo, proxy récalcitrant), on lance le polling sans
      // attendre — l'opérateur voit la situation tout de suite, le SSE prendra le relais s'il revient.
      graceTimer = setTimeout(() => {
        if (!aborted && !gotFirst) startPolling();
      }, SSE_GRACE_MS);
    };
    const stop = () => {
      sse?.abort();
      sse = null;
      stopPolling();
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (graceTimer !== null) {
        clearTimeout(graceTimer);
        graceTimer = null;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        stop();
        start();
      } else {
        stop();
      }
    };

    refreshRef.current = () => void pollOnce();
    if (document.visibilityState !== 'hidden') start();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      aborted = true;
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
      refreshRef.current = () => undefined;
    };
  }, [zoneId]);

  /**
   * Rafraîchissement immédiat (lecture ponctuelle), hors du flux : appelé après une action de gestion
   * (rattachement, changement d'état, repositionnement, retrait) pour refléter le résultat sans
   * attendre la prochaine trame.
   */
  const refresh = useCallback(() => refreshRef.current(), []);

  return { picture, loading, error, refresh };
}
